import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  CopilotAttribution,
  CopilotConversation,
  KnowledgeTreeSource,
  McpIntegrationSummary,
  McpTurnSource,
  RetrievedSource,
  SharePointSiteSummary,
  SharePointTreeItem,
  CopilotConversationSummary,
  CopilotSavedPrompt,
  CopilotStarterSuggestionsResponse,
  CopilotSettings,
  CopilotAgent,
  KnowledgeScope,
} from "@yazamco/shared-types";
import type {
  CopilotSavedPromptInput,
  CopilotSettingsInput,
  CreateCopilotConversationInput,
  UpdateCopilotConversationInput,
} from "@yazamco/validation";
import {
  createCopilotConversation,
  createCopilotSavedPrompt,
  deleteCopilotConversation,
  deleteCopilotSavedPrompt,
  fetchCopilotConversation,
  fetchCopilotConversations,
  fetchCopilotSavedPrompts,
  fetchCopilotStarterSuggestions,
  fetchCopilotSettings,
  fetchKnowledgeSites,
  fetchKnowledgeTree,
  connectMcpIntegration,
  disconnectMcpIntegration,
  fetchMcpIntegrations,
  setMcpIntegrationUse,
  updateCopilotConversation,
  updateCopilotSavedPrompt,
  updateCopilotSettings,
  fetchCopilotAgents,
} from "../../lib/copilot";
import type { CopilotSettingsPayload } from "../../lib/copilot";

/** Query keys — one place so components and tests stay consistent. */
export const copilotKeys = {
  all: ["copilot"] as const,
  conversations: (includeArchived: boolean) =>
    ["copilot", "conversations", includeArchived] as const,
  conversation: (id: string) => ["copilot", "conversation", id] as const,
  settings: ["copilot", "settings"] as const,
  savedPrompts: ["copilot", "savedPrompts"] as const,
  starterSuggestions: (scope: KnowledgeScope) =>
    ["copilot", "starterSuggestions", JSON.stringify(scope)] as const,
  agents: ["copilot", "agents"] as const,
  knowledgeSites: (search: string) => ["copilot", "knowledgeSites", search] as const,
  knowledgeTree: (
    source: KnowledgeTreeSource,
    siteId?: string,
    driveId?: string,
    itemId?: string,
  ) =>
    [
      "copilot",
      "knowledgeTree",
      source,
      siteId ?? "me",
      driveId ?? "root",
      itemId ?? "root",
    ] as const,
  mcpIntegrations: ["copilot", "mcpIntegrations"] as const,
};

export function useCopilotStarterSuggestions(
  scope: KnowledgeScope,
  enabled = true,
): UseQueryResult<CopilotStarterSuggestionsResponse> {
  return useQuery({
    queryKey: copilotKeys.starterSuggestions(scope),
    queryFn: () =>
      fetchCopilotStarterSuggestions({
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        knowledgeScope: scope,
      }),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * The approved integrations, with this user's own connection state.
 *
 * Cached only briefly, unlike the Phase 1 server list it replaces. That list
 * was pure deployment configuration and could not change without a restart;
 * this one carries CONNECTION STATE, which changes the moment somebody
 * connects, disconnects, or has a grant expire under them — and a "Connect"
 * button still showing after a successful connection is the one thing this
 * panel must not do.
 */
export function useMcpIntegrations(
  enabled = true,
): UseQueryResult<{ enabled: boolean; items: McpIntegrationSummary[] }> {
  return useQuery({
    queryKey: copilotKeys.mcpIntegrations,
    queryFn: fetchMcpIntegrations,
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Switch a whole business tool on or off for this user.
 *
 * One boolean per integration, and it is the composer's entire authority over
 * business tools: which TOOLS may run is the deployment's allowlist, set in
 * Settings → Business Integrations. The server's answer replaces the card,
 * because an "on" it declined to honour must not stay looking on, and the list
 * is invalidated too so the composer's chips follow.
 */
export function useSetMcpIntegrationUse(): UseMutationResult<
  { integration: McpIntegrationSummary },
  Error,
  { integrationId: string; enabled: boolean }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => setMcpIntegrationUse(input.integrationId, input.enabled),
    onSuccess: (result) => {
      client.setQueryData<{ enabled: boolean; items: McpIntegrationSummary[] }>(
        copilotKeys.mcpIntegrations,
        (previous) =>
          previous
            ? {
                ...previous,
                items: previous.items.map((item) =>
                  item.id === result.integration.id ? result.integration : item,
                ),
              }
            : previous,
      );
      void client.invalidateQueries({ queryKey: copilotKeys.mcpIntegrations });
    },
  });
}

/** Submit a personal API token. The token is never returned to us again. */
export function useConnectMcpIntegration(): UseMutationResult<
  unknown,
  Error,
  { integrationId: string; token: string }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => connectMcpIntegration(input.integrationId, input.token),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: copilotKeys.mcpIntegrations });
    },
  });
}

/** Disconnect. Preferences survive on the server and return on reconnect. */
export function useDisconnectMcpIntegration(): UseMutationResult<
  unknown,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) => disconnectMcpIntegration(integrationId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: copilotKeys.mcpIntegrations });
    },
  });
}

/**
 * The SharePoint sites the scope selector offers.
 *
 * Cached generously: the set of sites a person can reach changes on the scale
 * of weeks, and re-listing the tenant every time the dropdown opens spends a
 * Graph call to learn nothing.
 */
export function useKnowledgeSites(
  search = "",
  enabled = true,
): UseQueryResult<{ retrievalEnabled: boolean; items: SharePointSiteSummary[] }> {
  return useQuery({
    queryKey: copilotKeys.knowledgeSites(search),
    queryFn: () => fetchKnowledgeSites(search || undefined),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * One level of the folder picker.
 *
 * `siteId` is required for a SharePoint walk and meaningless for a OneDrive
 * one, where the drive is the caller's own and the server resolves it from the
 * token — so the query is enabled on that condition rather than on the id
 * always being present.
 */
export function useKnowledgeTree(
  source: KnowledgeTreeSource,
  siteId?: string,
  driveId?: string,
  itemId?: string,
  enabled = true,
): UseQueryResult<{ items: SharePointTreeItem[] }> {
  return useQuery({
    queryKey: copilotKeys.knowledgeTree(source, siteId, driveId, itemId),
    queryFn: () => fetchKnowledgeTree({ source, siteId, driveId, itemId }),
    enabled: enabled && (source === "oneDrive" || Boolean(siteId)),
    staleTime: 5 * 60_000,
  });
}

export function useCopilotConversations(
  includeArchived = false,
): UseQueryResult<{ items: CopilotConversationSummary[] }> {
  return useQuery({
    queryKey: copilotKeys.conversations(includeArchived),
    queryFn: () => fetchCopilotConversations(includeArchived),
    staleTime: 30_000,
  });
}

/**
 * One saved chat with its transcript. `staleTime: Infinity` on purpose: the
 * page appends each new turn to what it already has, so a refetch would only
 * ever risk replacing a live transcript with a stored one — which, for a user
 * who has retention switched off, is empty.
 */
export function useCopilotConversation(
  id: string | null,
): UseQueryResult<{ conversation: CopilotConversation }> {
  return useQuery({
    queryKey: copilotKeys.conversation(id ?? ""),
    queryFn: () => fetchCopilotConversation(id as string),
    enabled: id !== null,
    staleTime: Infinity,
  });
}

/**
 * Adds a completed turn to the cached transcript.
 *
 * Without this the cache would keep whatever the transcript looked like when
 * the chat was first fetched, and every later turn would be missing from it —
 * so leaving the chat and coming back would show the opening exchange and
 * nothing after it. The turn is appended rather than refetched on purpose: with
 * transcript retention switched off there is nothing on the server to refetch,
 * and a refetch would replace a live conversation with an empty one.
 */
export function useAppendCopilotTurn() {
  const client = useQueryClient();
  return useCallback(
    (turn: {
      conversationId: string;
      prompt: string;
      answer: string;
      attributions: CopilotAttribution[];
      retrievedSources: RetrievedSource[];
      mcpSources: McpTurnSource[];
    }) => {
      client.setQueryData<{ conversation: CopilotConversation }>(
        copilotKeys.conversation(turn.conversationId),
        (previous) => {
          // Nothing cached yet means the first fetch for this chat is still in
          // flight; it will arrive carrying this turn already.
          if (!previous) return previous;
          const now = new Date().toISOString();
          return {
            conversation: {
              ...previous.conversation,
              turnCount: previous.conversation.turnCount + 1,
              lastActivityAt: now,
              messages: [
                ...previous.conversation.messages,
                {
                  id: `local-${now}-user`,
                  role: "user" as const,
                  text: turn.prompt,
                  attributions: [],
                  retrievedSources: [],
                  mcpSources: [],
                  createdAt: now,
                },
                {
                  id: `local-${now}-assistant`,
                  role: "assistant" as const,
                  text: turn.answer,
                  attributions: turn.attributions,
                  // Mirrors what the server stores: the documents, never their
                  // passages. Keeps the cached transcript the same shape as a
                  // refetched one.
                  retrievedSources: turn.retrievedSources.map((source) => ({
                    ...source,
                    extracts: [],
                  })),
                  // Metadata, which is all the server keeps too — the business
                  // records themselves never entered this cache.
                  mcpSources: turn.mcpSources,
                  createdAt: now,
                },
              ],
            },
          };
        },
      );
    },
    [client],
  );
}

export function useCopilotSettings(): UseQueryResult<CopilotSettingsPayload> {
  return useQuery({
    queryKey: copilotKeys.settings,
    queryFn: fetchCopilotSettings,
    staleTime: 60_000,
  });
}

export function useCopilotSavedPrompts(): UseQueryResult<{ items: CopilotSavedPrompt[] }> {
  return useQuery({
    queryKey: copilotKeys.savedPrompts,
    queryFn: fetchCopilotSavedPrompts,
    staleTime: 60_000,
  });
}

/**
 * The agents the composer offers.
 *
 * Read-only, unlike the four thinking-model hooks it replaced: since migration
 * 0059 an agent is written in Copilot administration, not from the chat's own
 * settings dialog, so there is one form for it instead of two.
 */
export function useCopilotAgents(): UseQueryResult<{ items: CopilotAgent[] }> {
  return useQuery({
    queryKey: copilotKeys.agents,
    queryFn: fetchCopilotAgents,
    staleTime: 60_000,
  });
}

export function useCreateCopilotConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCopilotConversationInput = {}) =>
      createCopilotConversation(input),
    onSuccess: () => client.invalidateQueries({ queryKey: copilotKeys.all }),
  });
}

export function useUpdateCopilotConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCopilotConversationInput }) =>
      updateCopilotConversation(id, input),
    onSuccess: ({ conversation }) => {
      // The detail cache carries the instruction snapshot the composer reads,
      // so a per-chat settings change has to land there and not only in the list.
      client.setQueryData(copilotKeys.conversation(conversation.id), (previous) =>
        previous
          ? {
              conversation: {
                ...conversation,
                messages: (previous as { conversation: CopilotConversation })
                  .conversation.messages,
              },
            }
          : { conversation },
      );
      void client.invalidateQueries({ queryKey: ["copilot", "conversations"] });
    },
  });
}

export function useDeleteCopilotConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCopilotConversation(id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["copilot", "conversations"] }),
  });
}

export function useUpdateCopilotSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CopilotSettingsInput) => updateCopilotSettings(input),
    onSuccess: (data) => client.setQueryData(copilotKeys.settings, data),
  });
}

export function useCreateCopilotSavedPrompt() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CopilotSavedPromptInput) => createCopilotSavedPrompt(input),
    onSuccess: () => client.invalidateQueries({ queryKey: copilotKeys.savedPrompts }),
  });
}

export function useUpdateCopilotSavedPrompt() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CopilotSavedPromptInput }) =>
      updateCopilotSavedPrompt(id, input),
    onSuccess: () => client.invalidateQueries({ queryKey: copilotKeys.savedPrompts }),
  });
}

export function useDeleteCopilotSavedPrompt() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCopilotSavedPrompt(id),
    onSuccess: () => client.invalidateQueries({ queryKey: copilotKeys.savedPrompts }),
  });
}

// The create/update/delete thinking-model mutations that stood here are gone
// with migration 0059. Writing an agent goes through `lib/agents.ts` from
// Copilot administration, which is the one place that form now lives.
